import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSelector from '@/components/LanguageSelector';
import { useI18nStore } from '@/store/i18nStore';

export default function LandingPage() {
  const t = useI18nStore((s) => s.t);

  return (
    <>
      <Helmet>
        <title>React-City — {t('landing.title')}</title>
        <meta
          name="description"
          content={t('landing.subtitle')}
        />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-[#1b1b1f] transition-colors">
        {/* Theme & Language Toggle */}
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
          <LanguageSelector />
          <ThemeToggle />
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden bg-hero-gradient">
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="text-center">
              <span className="text-6xl mb-6 block">�️</span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance">
                Il Movimento del Dono
              </h1>
              <p className="text-xl md:text-2xl text-white/90 dark:text-surface-300 max-w-2xl mx-auto mb-10 text-balance">
                Dona, ricevi, rigenera. Un ciclo virtuoso di mutuo sostegno
                che unisce la tua comunità.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register" className="btn-lg bg-white text-primary-700 hover:bg-primary-50 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-400 font-bold rounded-2xl px-8 py-4 shadow-lg no-underline">
                  Inizia ora
                </Link>
                <Link to="/login" className="btn-lg bg-white/10 text-white hover:bg-white/20 border border-white/30 dark:border-surface-600 rounded-2xl px-8 py-4 no-underline">
                  Accedi
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Come funziona */}
        <section className="py-20 bg-white dark:bg-[#1b1b1f]" id="come-funziona">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4 text-surface-900 dark:text-white">Come funziona</h2>
            <p className="text-center text-surface-500 dark:text-surface-400 mb-12 max-w-xl mx-auto">
              Il City-Box è la cellula del movimento. 11 persone, 3 ruoli, un ciclo che si ripete.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="card text-center">
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-500/15 text-primary-600 dark:text-primary-400 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold mb-2 text-surface-900 dark:text-white">Dona</h3>
                <p className="text-surface-500 dark:text-surface-400 text-sm">
                  Entra in un City-Box come <strong>Nuovo Donatore</strong>.
                  Il tuo contributo di €100 alimenta il ciclo del dono.
                </p>
              </div>

              {/* Step 2 */}
              <div className="card text-center">
                <div className="w-16 h-16 bg-accent-100 dark:bg-accent-500/15 text-accent-600 dark:text-accent-400 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold mb-2 text-surface-900 dark:text-white">Accompagna</h3>
                <p className="text-surface-500 dark:text-surface-400 text-sm">
                  Diventi <strong>Bridge</strong> nel box successivo.
                  Guidi i nuovi donatori e sostieni la comunità.
                </p>
              </div>

              {/* Step 3 */}
              <div className="card text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold mb-2 text-surface-900 dark:text-white">Ricevi</h3>
                <p className="text-surface-500 dark:text-surface-400 text-sm">
                  Diventi <strong>Receiver</strong> e ricevi €800.
                  Puoi reinvestire nel ciclo o uscire.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Numeri */}
        <section className="py-20 bg-surface-50 dark:bg-[#1e1e22]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="card-flat">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">11</p>
                <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">Membri per box</p>
              </div>
              <div className="card-flat">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">€100</p>
                <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">Donazione</p>
              </div>
              <div className="card-flat">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">€800</p>
                <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">Payout receiver</p>
              </div>
              <div className="card-flat">
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">80%</p>
                <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">Al receiver</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 bg-surface-900 dark:bg-[#141416] text-surface-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4 text-sm">
              <Link to="/come-funziona" className="text-surface-400 hover:text-white transition-colors no-underline">
                Come funziona
              </Link>
              <span className="hidden sm:inline text-surface-600">·</span>
              <Link to="/privacy" className="text-surface-400 hover:text-white transition-colors no-underline">
                Privacy Policy
              </Link>
              <span className="hidden sm:inline text-surface-600">·</span>
              <Link to="/cookie-policy" className="text-surface-400 hover:text-white transition-colors no-underline">
                Cookie Policy
              </Link>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} React-City — Movimento del Dono.
              Tutti i diritti riservati.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
